import type { ReactNode } from "react";
import { BRAND, REGION_NOT_OFFERED } from "../design/copy.js";
import { BloomBackground } from "./background.js";
import { Rule } from "./primitives.js";

/** The doors that stay open from where the gate refused, as the app wires them. */
export interface RegionOpenDoors {
  /**
   * What this browser keeps here: a guest's work or an account (then the
   * settings door, with the export and the deletion, and for an account
   * "log out."), or nothing (then "sign in.", for an account made elsewhere).
   */
  readonly kept: "guest" | "account" | null;
  readonly onSettings: () => void;
  readonly onLogOut: () => void;
  readonly onSignIn: () => void;
  readonly onPrivacy: () => void;
  readonly onTerms: () => void;
}

const DOOR = "cursor-pointer border-0 bg-transparent p-0 font-sans text-nav text-ink hover:bg-marker";

/**
 * THE REGION GATE'S SCREEN (Andre's rulings of 2026-10-27 and answers of
 * 2026-10-28). The api answered a door that writes or calls a model with 451
 * REGION_NOT_OFFERED (contracts' RegionNotOfferedProblemSchema), or sent the
 * browser here from Google's callback: the service is not offered in the EU,
 * the EEA or the UK for now. The app shows this instead of itself, until the
 * page is reloaded. Nothing on it tries the refused door again, so a refusal
 * can never become a loop; its doors are the ones the gate keeps open (the
 * privacy and terms pages, the settings sheet with the export and the
 * deletion, logging out, signing in), each the app's own. The words are
 * REGION_NOT_OFFERED in design/copy.ts; the privacy page's card on the bloom is
 * the layout, and DB-IP's credit is on it because the screen shows a result
 * of DB-IP's table.
 */
export function RegionNotOffered({ doors }: { readonly doors?: RegionOpenDoors }): ReactNode {
  const words = REGION_NOT_OFFERED;
  return (
    <div className="relative flex min-h-svh items-start justify-center sm:py-16">
      <BloomBackground />
      <main
        data-testid="region-not-offered"
        className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[44%] sm:min-w-[34rem] sm:max-w-[46rem] sm:px-16 sm:py-12"
      >
        <div className="font-sans text-nav">{BRAND.short}</div>
        <Rule className="mt-2" />
        <h1 className="mt-6 mb-0 font-display text-display font-bold">{words.title}</h1>
        <p className="mt-6 mb-0 font-sans text-body">{words.body}</p>
        <p className="mt-4 mb-0 font-sans text-body">
          {words.contact.before}
          <a href={`mailto:${words.contact.address}`} className="text-link underline">
            {words.contact.address}
          </a>
          {words.contact.after}
        </p>
        {doors === undefined ? null : (
          <>
            <p className="mt-6 mb-0 font-sans text-body">{doors.kept === null ? words.signInLine : words.keptLine}</p>
            <ul className="mt-3 mb-0 flex list-none flex-wrap gap-x-6 gap-y-2 p-0" aria-label={words.title}>
              {doors.kept === null ? (
                <li>
                  <button type="button" className={DOOR} onClick={doors.onSignIn}>
                    {words.signIn}
                  </button>
                </li>
              ) : (
                <li>
                  <button type="button" className={DOOR} onClick={doors.onSettings}>
                    {words.settings}
                  </button>
                </li>
              )}
              {doors.kept === "account" ? (
                <li>
                  <button type="button" className={DOOR} onClick={doors.onLogOut}>
                    {words.logOut}
                  </button>
                </li>
              ) : null}
              <li>
                <button type="button" className={DOOR} onClick={doors.onPrivacy}>
                  {words.privacy}
                </button>
              </li>
              <li>
                <button type="button" className={DOOR} onClick={doors.onTerms}>
                  {words.terms}
                </button>
              </li>
            </ul>
          </>
        )}
        <Rule className="mt-10" />
        <p className="mt-4 mb-0 font-sans text-micro">
          <a href={words.credit.href} target="_blank" rel="noopener noreferrer" className="text-link underline">
            {words.credit.link}
          </a>
        </p>
      </main>
    </div>
  );
}
